import { Box, Button, Link, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CarrierLogo, formatCarrierLogoCarrier } from "./CarrierLogo";
import { resolveCarrierTrackUrl } from "./constants";
import { resolveShipmentDisplay } from "@features/orders-features/functions/shipmentDisplay";
import { OrderLineItemDto } from "@lib/ShiOneClient";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";

interface ShipmentMetaProps {
  items: OrderLineItemDto[];
  status: string;
  /** Order placement date, used to decide whether to show precise timestamps. */
  orderDate?: Date | string | null;
  /** When provided, "Track Package" opens a drawer instead of an external link */
  onTrackPackage?: () => void;
  telemetrySection?: string;
}

export interface ShipmentGroup {
  key: string;
  trackingNumber: string | null | undefined;
  carrier: string | null | undefined;
  carrierEnum: string | null | undefined;
  shippedOn: Date | null | undefined;
  deliveryOn: Date | null | undefined;
  estimatedDeliveryOn: Date | null | undefined;
  items: OrderLineItemDto[];
}

function buildShipmentGroupKey(item: OrderLineItemDto, index: number) {
  const trackingNumber = item.trackingNumber?.trim();

  if (trackingNumber) {
    return `tracking:${trackingNumber}`;
  }

  const itemGroupId = item.itemGroupId?.trim();
  if (itemGroupId) {
    return `group:${itemGroupId}`;
  }

  const carrier = item.carrier?.trim() || "unknown";
  const shippedOn = item.shippedOn?.toISOString() || "no-ship-date";
  const deliveryOn = item.deliveryOn?.toISOString() || "no-delivery-date";
  const deliveryPerson = item.deliveryPerson?.trim() || "no-recipient";

  return `untracked:${carrier}:${shippedOn}:${deliveryOn}:${deliveryPerson}:${index}`;
}

export function groupItemsByShipment(
  items: OrderLineItemDto[],
): ShipmentGroup[] {
  const groupMap = new Map<string, ShipmentGroup>();

  for (const [index, item] of items.entries()) {
    const key = buildShipmentGroupKey(item, index);

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        key,
        trackingNumber: item.trackingNumber?.trim(),
        carrier: item.carrier?.trim(),
        carrierEnum: item.carrierEnum,
        shippedOn: item.shippedOn,
        deliveryOn: item.deliveryOn,
        estimatedDeliveryOn: item.estimatedDeliveryOn,
        items: [],
      });
    }

    groupMap.get(key)!.items.push(item);
  }

  return Array.from(groupMap.values());
}

function ShipmentMetaBlock({
  group,
  status,
  orderDate,
  onTrackPackage,
  telemetrySection,
}: Readonly<{
  group: ShipmentGroup;
  status: string;
  orderDate?: Date | string | null;
  onTrackPackage?: () => void;
  telemetrySection?: string;
}>) {
  if (group.items.length === 0) return null;

  const hasTracking = !!group.trackingNumber;
  const trackUrl = resolveCarrierTrackUrl(group.carrierEnum, group.trackingNumber);

  const trackTelemetry = telemetrySection
    ? telemetryProps({
        name: "orders_track_package",
        section: telemetrySection,
        label: "Track package",
      })
    : undefined;

  const display = resolveShipmentDisplay({
    carrierEnum: group.carrierEnum,
    status,
    deliveryOn: group.deliveryOn,
    estimatedDeliveryOn: group.estimatedDeliveryOn,
    orderDate,
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      <Typography variant={"body1"} sx={{ fontWeight: 500 }}>
        {display.statusText}
      </Typography>
      {display.detailText && (
        <Typography variant="body2" color="text.secondary">
          {display.detailText}
        </Typography>
      )}
      {hasTracking && (
        <Typography variant="body2" color="text.secondary">
          Tracking #:{" "}
          <Link
            href={trackUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              verticalAlign: "bottom",
            }}
          >
            {group.trackingNumber}
            <OpenInNewIcon fontSize="inherit" color="primary" />
          </Link>
        </Typography>
      )}
      {hasTracking && (
        <CarrierLogo
          carrier={formatCarrierLogoCarrier(group.carrierEnum)}
          carrierName={group.carrier}
        />
      )}
      {hasTracking && (
        <Box sx={{ ml: "auto", pr: 2 }}>
          {onTrackPackage ? (
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={onTrackPackage}
              sx={{ fontSize: 12, letterSpacing: 0.5 }}
              {...trackTelemetry}
            >
              TRACK PACKAGE
            </Button>
          ) : (
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              href={trackUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: 12, letterSpacing: 0.5 }}
              {...trackTelemetry}
            >
              TRACK PACKAGE
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

export function ShipmentMeta({ items, status, orderDate, onTrackPackage, telemetrySection }: Readonly<ShipmentMetaProps>) {
  if (!items?.length) return null;

  const shipmentGroups = groupItemsByShipment(items);

  if (shipmentGroups.length <= 1) {
    return <ShipmentMetaBlock group={shipmentGroups[0]} status={status} orderDate={orderDate} onTrackPackage={onTrackPackage} telemetrySection={telemetrySection} />;
  }

  return (
    <Stack spacing={1}>
      {shipmentGroups.map((group) => (
        <ShipmentMetaBlock
          key={group.key}
          group={group}
          status={status}
          orderDate={orderDate}
          onTrackPackage={onTrackPackage}
          telemetrySection={telemetrySection}
        />
      ))}
    </Stack>
  );
}



